import { LEGAL_COMPANY as C } from './company';
import type { LegalDoc } from './types';

/**
 * Public account-deletion page (customer app, /delete-account).
 *
 * Google Play requires a public URL where users of the FinminOS Android app
 * can ask for their account and data to be deleted, naming the app and the
 * developer and listing the steps. Retention periods mirror the "How long we
 * keep data" section of the Privacy Policy; change them together.
 */
export const DELETE_ACCOUNT_URL = 'https://finminos.com/delete-account';

const SUBJECT = 'Account deletion request';
const MAILTO = `mailto:${C.supportEmail}?subject=${encodeURIComponent(SUBJECT)}`;

export const DELETE_ACCOUNT: LegalDoc = {
  title: 'Delete your FinminOS account',
  summary:
    'How to ask us to delete your FinminOS account and personal data, what we delete, and the few records the law requires us to keep.',
  lastUpdated: '21 October 2026',
  intro: [
    `This page applies to the FinminOS app on Android and the FinminOS website. Both are developed and operated by ${C.entity}.`,
    {
      note: `To delete your account, email ${C.supportEmail} with the subject “${SUBJECT}”. We complete deletion within 90 days of verifying your request.`,
    },
  ],
  sections: [
    {
      id: 'how-to-request',
      heading: 'How to request deletion',
      blocks: [
        {
          ordered: true,
          list: [
            `Email ${C.supportEmail} with the subject “${SUBJECT}”.`,
            'Send it from the email address registered on your FinminOS account. If you no longer use that address, tell us the mobile number registered on your account instead.',
            'We confirm that the request comes from you before acting on it, for example by sending a one-time password (OTP) to your registered mobile number.',
            'Once your identity is confirmed, we delete your account and data as described below and let you know by email when it is done.',
          ],
        },
        { lead: 'Write to us at', link: C.supportEmail, href: MAILTO },
      ],
    },
    {
      id: 'deactivate',
      heading: 'Deactivate in the app first (optional)',
      blocks: [
        'If you want to stop all access to your account straight away, you can deactivate it yourself. In the FinminOS app, open Profile, choose Deactivate account and confirm with the OTP sent to your registered mobile number. You are signed out and nobody can sign in to the account.',
        'Deactivation on its own does not delete your account. To have it deleted, also send us a deletion request as described above.',
      ],
    },
    {
      id: 'what-we-delete',
      heading: 'What we delete',
      blocks: [
        {
          list: [
            'Your profile: name, email address, mobile number, date of birth and the PANs you added.',
            'Family members and other profiles linked to your account, and the details you entered for them.',
            'Documents you uploaded.',
            'Chat messages with FinminOS and with the tax expert handling your service.',
            'Support tickets.',
            'Reward points and referral records. Unused points are cancelled and cannot be restored.',
          ],
        },
        'We also instruct the tax experts and vendors who process data on our behalf to delete the data they hold for your account.',
      ],
    },
    {
      id: 'what-we-keep',
      heading: 'What we keep, and for how long',
      blocks: [
        'Indian law requires us to keep some records even after an account is deleted. We keep only these, only for the period the law requires, and then securely delete or anonymise them:',
        {
          list: [
            'Payment, invoice and refund records: for the period required by tax, accounting and RBI rules, currently 8 years.',
            'Tax documents, computations, filed returns and service records: for 8 years from the end of the financial year they relate to, so that assessments, reassessments and notices under the Income-tax Law can be answered.',
            'Security and access logs: for the period required by CERT-In directions (180 days) and the DPDP Rules.',
            'Records of an open grievance or dispute: until the matter is closed and any limitation period has passed.',
          ],
        },
      ],
    },
    {
      id: 'timeline',
      heading: 'How long it takes',
      blocks: [
        'We erase or anonymise your account and data within 90 days of verifying your request, as set out in our Privacy Policy. Deletion cannot be undone: your documents and history cannot be recovered, and you would need to sign up again to use FinminOS.',
      ],
    },
    {
      id: 'privacy',
      heading: 'More about your data',
      blocks: [
        'Our Privacy Policy explains how we collect, use, share and keep personal data, and the other rights you have over it.',
        { lead: 'Read the', link: 'Privacy Policy', href: '/privacy' },
      ],
    },
  ],
};
